/**
 * ScanlineOverlay — single fixed full-viewport overlay that draws the CRT
 * scanline texture across everything. Pure CSS, no animation.
 *
 * Toggleable via localStorage["sherlock.scanlines"] (default "on").
 * The window.sherlock.theme.toggleScanlines() helper from EasterEggs.tsx
 * dispatches a "sherlock:scanlines" event that this component listens for.
 */

import { useEffect, useState } from 'react';
import { Box } from '@mui/material';

const STORAGE_KEY = 'sherlock.scanlines';

function readPref(): boolean {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v !== 'off';
  } catch {
    return true;
  }
}

export function ScanlineOverlay(): React.ReactElement | null {
  const [enabled, setEnabled] = useState<boolean>(readPref);

  useEffect(() => {
    function onToggle(e: Event): void {
      const detail = (e as CustomEvent<{ enabled: boolean }>).detail;
      setEnabled(detail.enabled);
    }
    window.addEventListener('sherlock:scanlines', onToggle as EventListener);
    return () => {
      window.removeEventListener('sherlock:scanlines', onToggle as EventListener);
    };
  }, []);

  if (!enabled) return null;

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 1,
        backgroundImage:
          'repeating-linear-gradient(0deg, rgba(0,0,0,0.16) 0px, rgba(0,0,0,0.16) 1px, transparent 1px, transparent 3px)',
        mixBlendMode: 'multiply',
      }}
    />
  );
}
